import { useTranslation } from "react-i18next";
import {
  Invitation,
  Organization,
} from "../../../services/organization/organizationModel";
import { User } from "../../../services/user/userModel";
import {
  getOrganizationMemberStatus,
  organizationService,
} from "../../../services/organization/organizationService";
import { Dispatch, SetStateAction } from "react";
import { cx } from "../../../react-helpers/css";
import SortableTh from "../../../components/SortableTh";
import { TableSort } from "../../../react-helpers/array";
import { authService } from "../../../services/auth/authService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";

const { getLoggedUserId } = authService();
const { deleteInvitation, removeUserFromOrganization } = organizationService();

export type UsersSortKey = "name" | "status";

const UsersTable = ({
  organization,
  users,
  setInviteAndManageUser,
  sort,
  onSort,
}: {
  organization: Organization;
  users: (User & { invitation?: Invitation })[];
  setInviteAndManageUser: Dispatch<
    SetStateAction<{ user?: User; invitation?: Invitation } | null>
  >;
  sort: TableSort<UsersSortKey> | null;
  onSort: (key: UsersSortKey) => void;
}) => {
  const { t } = useTranslation(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  return users.length > 0 ? (
    <table className="lblock table">
      <thead>
        <tr>
          <SortableTh sortKey="name" sort={sort} onSort={onSort}>
            {t("organization:users.table-headers.NAME")}
          </SortableTh>
          <SortableTh sortKey="status" sort={sort} onSort={onSort}>
            {t("organization:users.table-headers.STATUS")}
          </SortableTh>
          <th>{t("organization:users.table-headers.EMAIL")}</th>
          <th>{t("organization:users.table-headers.ROLE")}</th>
          <th>{t("organization:users.table-headers.USED_CREDITS")}</th>
          <th>{t("organization:users.table-headers.ACTION")}</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => {
          const userStatus = getOrganizationMemberStatus(organization.id, user);

          return (
            <tr key={`user-${user.id}`}>
              <td>
                {user.lastname} {user.firstname}
              </td>
              <td>
                <span
                  className={cx([
                    "user-status-chip --block",
                    userStatus === "active" && "--active",
                    userStatus === "disabled" && "--disabled",
                  ])}
                >
                  {userStatus === "active" &&
                    t("organization:users.status.ACTIVE")}
                  {userStatus === "pending" &&
                    t("organization:users.status.PENDING")}
                  {userStatus === "disabled" &&
                    t("organization:users.status.DISABLED")}
                </span>
              </td>
              <td>{user.email}</td>
              <td>
                {user.invitation?.membershipType === "GUEST"
                  ? t("organization:users.role.GUEST")
                  : user.adminOrga ||
                      user.invitation?.membershipType === "ADMIN"
                    ? t("organization:users.role.ADMIN")
                    : t("organization:users.role.MEMBER")}
              </td>
              <td>
                {(user.capsules?.length ?? 0) > 0 && user.capsules?.length}
              </td>
              <td>
                <div className="lrow --nowrap --center--v --gap--xs">
                  <button
                    className="btn--icon --simple --edit"
                    onClick={() => {
                      setInviteAndManageUser(
                        user.invitation
                          ? {
                              invitation: {
                                ...user.invitation,
                                firstname: user.firstname,
                                lastname: user.lastname,
                                email: user.email,
                              },
                            }
                          : {
                              user,
                            },
                      );
                    }}
                  />
                  {user.id !== getLoggedUserId() && (
                    <button
                      className="btn--icon --simple --delete"
                      title={t("organization:users.DELETE_USER_TIP")}
                      onClick={() => {
                        const invitation = user.invitation;
                        confirm(
                          userStatus === "pending"
                            ? "organization:users.confirm-delete.PENDING_MESSAGE"
                            : "organization:users.confirm-delete.MESSAGE",
                          () =>
                            (invitation
                              ? deleteInvitation(organization.id, invitation.id)
                              : removeUserFromOrganization(
                                  organization.id,
                                  user.id,
                                )
                            ).then(() => reload()),
                          true,
                          {
                            name: `${user.firstname} ${user.lastname}`,
                          },
                        );
                      }}
                    />
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  ) : (
    <div className="info --compact lblock">
      {t("organization:users.NO_USERS_FOUND")}
    </div>
  );
};

export default UsersTable;
